import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import App from "./App";
import Config from "./utils/Config";
import { camelCaseKeys } from "./utils/StringUtils";
import * as Sentry from "@sentry/react";
import { browserTracingIntegration, captureConsoleIntegration } from "@sentry/react";
import { SubscriptionPlan } from "./pages/SubscriptionPage/SubscriptionPlan";
import axios from "axios";
import * as serviceWorker from "./serviceWorker";

const urlParams = new URLSearchParams(window.location.search);
const authQueryString = urlParams.toString();
const installUrlAws = `${Config.apiGateway.URL}/authentication/install/${Config.appName}?${authQueryString}`;
const installUrlFly = `${Config.flyApi.URL}/install/${Config.appName}?${authQueryString}`;

Sentry.init({
  dsn: Config.sentryDsn,
  integrations: [
    browserTracingIntegration(),
    captureConsoleIntegration({
      levels: ['error'],
     }),
  ],

  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 1.0,
});

const authenticate = async () => {
  try {
    // Try Fly.io first
    try {
      const { data, status } = await axios.get(installUrlFly);
      if (status === 200) {
        handleSuccessfulAuth(data);
        return;
      }
    } catch (flyError) {
      console.log("Fly.io authentication failed, falling back to AWS:", flyError);
    }

    // Fallback to AWS
    const { data, status } = await axios.get(installUrlAws);
    if (status === 200) {
      handleSuccessfulAuth(data);
    } else {
      throw new Error(`Unexpected status code: ${status}`);
    }
  } catch (error) {
    console.error("Authentication failed:", error);
    document.getElementById("root").innerText = "Authentication failed, please try again";
  }
};

const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    // Ensure it executes in order (no `async` or `defer`)
    script.onload = () => {
      // all code in the script has run at this point
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

const loadAppBridge = () => {
  return loadScript("https://cdn.shopify.com/shopifycloud/app-bridge.js");
};

const fetchSessionToken = () => {
  try {
    if (window.shopify)
      window.shopify.idToken().then(x => console.log("session-token fetched"));
  } catch (error) {
    console.log("session-token authentication failed:", error);
  } 
};

// Helper function to handle successful authentication
const handleSuccessfulAuth = async (data) => {
  const { OnboardingStatus, Plan, FreeDiscountsLeft, NeedsVatNumber, MissingScopes, RedirectUrl } = data;
  const commonProps = {
    plan: Plan,
    freeDiscountsLeft: FreeDiscountsLeft,
    requestVatNumber: NeedsVatNumber,
    missingScopes: MissingScopes,
    onboardingStatus: camelCaseKeys(OnboardingStatus),
  };
  
  if (Plan >= SubscriptionPlan.LegacyStarter) {
    await loadAppBridge();
    fetchSessionToken();
    try {
      ReactDOM.render(<App {...commonProps} />, document.getElementById("root"));
    } catch (error) {
      console.error("ReactDOM.render failed:", error);
      Sentry.captureException(error, {
        tags: {
          location: "ReactDOM.render",
          plan: Plan,
          onboardingStatus: JSON.stringify(OnboardingStatus),
        },
        extra: {
          errorMessage: error.message,
          rootElement: document.getElementById("root") ? "exists" : "missing",
          commonProps: JSON.stringify(commonProps)
        }
      });
      if (document.getElementById("root")) {
        document.getElementById("root").innerText = "Failed to load app, please refresh the page";
      }
    }
  } else {
    window.top === window.self
      ? window.location.assign(RedirectUrl)
      : ReactDOM.render(<App {...commonProps} redirectUrl={RedirectUrl} />, document.getElementById("root"));
  }
};

if (!authQueryString) {
  document.getElementById("root").innerText = "Cannot authenticate, please reload app";
} else {
  authenticate();
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
